/**
 * Audit State Machine
 * Defines audit statuses, transitions, and permission logic for both regular and vendor audits
 */

// ============================================
// LOCATION AUDIT STATUSES (Used by both regular and vendor activities)
// ============================================

export const AUDIT_STATUS = {
  PRELIMINARY_AUDIT_CREATED: 'preliminary_audit_created',
  PRELIMINARY_AUDIT_PENDING: 'preliminary_audit_pending',
  PRELIMINARY_AUDIT_SUBMITTED: 'preliminary_audit_submitted',
  PRELIMINARY_AUDIT_FINALIZED: 'preliminary_audit_finalized',
  PRELIMINARY_AUDIT_REPORT_UPLOADING: 'preliminary_audit_report_uploading',
  PRELIMINARY_AUDIT_REPORT_FAILED: 'preliminary_audit_report_failed',
  FINAL_AUDIT_PENDING: 'final_audit_pending',
  FINAL_AUDIT_EDITING: 'final_audit_editing',
  FINAL_AUDIT_SUBMITTED: 'final_audit_submitted',
  FINAL_AUDIT_PUBLISHED: 'final_audit_published',
  FINAL_AUDIT_REPORT_UPLOADING: 'final_audit_report_uploading',
  FINAL_AUDIT_REPORT_FAILED: 'final_audit_report_failed',
  FINAL_AUDIT_REPORT_UPLOADED: 'final_audit_report_uploaded',
  OVERDUE: 'overdue'
} as const;

export const AUDIT_STATUS_LABELS = {
  [AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING]: 'Preliminary Audit Pending',
  [AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED]: 'Preliminary Audit Submitted',
  [AUDIT_STATUS.PRELIMINARY_AUDIT_FINALIZED]: 'Preliminary Audit Publishing',
  [AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_UPLOADING]: 'Preliminary Audit Publishing',
  [AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_FAILED]: 'Preliminary Audit Failed',
  [AUDIT_STATUS.FINAL_AUDIT_PENDING]: 'Preliminary Audit Published, Final Audit Pending',
  [AUDIT_STATUS.FINAL_AUDIT_EDITING]: 'Final Audit Editing',
  [AUDIT_STATUS.FINAL_AUDIT_SUBMITTED]: 'Final Audit Submitted',
  [AUDIT_STATUS.FINAL_AUDIT_PUBLISHED]: 'Final Audit Publishing',
  [AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADING]: 'Final Audit Publishing',
  [AUDIT_STATUS.FINAL_AUDIT_REPORT_FAILED]: 'Final Audit Failed',
  [AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED]: 'Final Audit Published',
  [AUDIT_STATUS.OVERDUE]: 'Overdue'
} as const;

export const AUDIT_STATUS_COLOR_CODES = {
  [AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING]: 'text-red',
  [AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED]: 'text-amber',
  [AUDIT_STATUS.PRELIMINARY_AUDIT_FINALIZED]: 'text-amber',
  [AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_UPLOADING]: 'text-amber',
  [AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_FAILED]: 'text-red',
  [AUDIT_STATUS.FINAL_AUDIT_PENDING]: 'text-amber',
  [AUDIT_STATUS.FINAL_AUDIT_EDITING]: 'text-amber',
  [AUDIT_STATUS.FINAL_AUDIT_SUBMITTED]: 'text-amber',
  [AUDIT_STATUS.FINAL_AUDIT_PUBLISHED]: 'text-amber',
  [AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADING]: 'text-amber',
  [AUDIT_STATUS.FINAL_AUDIT_REPORT_FAILED]: 'text-red',
  [AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED]: 'text-green',
  [AUDIT_STATUS.OVERDUE]: 'text-red'
} as const;

// ============================================
// ACTIVITY STATUSES
// ============================================

// Todo Activity Statuses (from existing ACTIVITY_STATUS)
export const TODO_STATUS = {
  PENDING: 'Pending',
  ACTIVITY_SAVED: 'ActivitySaved',
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  OVERDUE: 'Overdue',
  PUBLISHED: 'Published'
} as const;

// Activity Types
export const AUDITTED_TYPE = {
  NO_AUDIT: 'No Audit',
  PHYSICAL_AUDIT: 'Physical Audit',
  AUDIT: 'Audit'
} as const;

// User Roles
export const USER_ROLE = {
  VENDOR_USER: 'Vendor User',
  VENDOR_ADMIN: 'Vendor Admin',
  AUDITOR_USER: 'Auditor User',
  AUDITOR_ADMIN: 'Auditor Admin'
} as const;

// ============================================
// PERMISSION CHECKS - VENDOR ACTIVITIES
// ============================================

/**
 * Check if vendor can save/edit activity
 */
export function canVendorSaveActivity(
  todo: any,
  audit: any,
  userRole: string
): boolean {
  // Check if todo is published
  if (todo.published === true) {
    return false;
  }

  // Check if todo status is submitted
  if (todo.status === TODO_STATUS.SUBMITTED) {
    return false;
  }

  // Check audit status
  const allowedAuditStatuses = [
    AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING,
    AUDIT_STATUS.FINAL_AUDIT_PENDING,
    AUDIT_STATUS.FINAL_AUDIT_EDITING
  ];

  if (!audit || !allowedAuditStatuses.includes(audit.status)) {
    return false;
  }

  return true;
}

/**
 * Check if auditor can edit todo
 */
export function canAuditorEditTodo(
  todo: any,
  audit: any
): boolean {
  // Check if todo is published
  if (todo.published === true) {
    return false;
  }

  // Check todo status
  const allowedTodoStatuses = [
    TODO_STATUS.SUBMITTED,
    TODO_STATUS.APPROVED,
    TODO_STATUS.REJECTED
  ];

  if (!allowedTodoStatuses.includes(todo.status)) {
    return false;
  }

  // Check audit status
  const allowedAuditStatuses = [
    AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED,
    AUDIT_STATUS.FINAL_AUDIT_SUBMITTED,
    AUDIT_STATUS.FINAL_AUDIT_EDITING,
    AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_FAILED,
    AUDIT_STATUS.FINAL_AUDIT_REPORT_FAILED
  ];

  if (!audit || !allowedAuditStatuses.includes(audit.status)) {
    return false;
  }

  return true;
}

/**
 * Check if vendor admin can submit todos to auditor
 */
export function canSubmitToAuditor(
  audit: any,
  userRole: string
): boolean {
  if (userRole !== USER_ROLE.VENDOR_ADMIN) {
    return false;
  }

  const allowedStatuses = [
    AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING,
    AUDIT_STATUS.FINAL_AUDIT_PENDING,
    AUDIT_STATUS.FINAL_AUDIT_EDITING
  ];

  return audit && allowedStatuses.includes(audit.status);
}

/**
 * Check if auditor can finalize preliminary audit
 */
export function canFinalizePreliminaryAudit(
  audit: any,
  todos: any[],
  userRole: string
): boolean {
  if (userRole !== USER_ROLE.AUDITOR_ADMIN) {
    return false;
  }

  // Check audit status
  const allowedStatuses = [
    AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED,
    AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_FAILED
  ];

  if (!audit || !allowedStatuses.includes(audit.status)) {
    return false;
  }

  // Check if all todos are approved or rejected
  return todos.every(
    (todo) =>
      todo.status === TODO_STATUS.APPROVED ||
      todo.status === TODO_STATUS.REJECTED
  );
}

/**
 * Check if auditor can publish final audit
 */
export function canPublishFinalAudit(
  audit: any,
  todos: any[],
  userRole: string
): boolean {
  if (userRole !== USER_ROLE.AUDITOR_ADMIN) {
    return false;
  }

  // Check audit status
  const allowedStatuses = [
    AUDIT_STATUS.FINAL_AUDIT_SUBMITTED,
    AUDIT_STATUS.FINAL_AUDIT_EDITING,
    AUDIT_STATUS.FINAL_AUDIT_REPORT_FAILED
  ];

  if (!audit || !allowedStatuses.includes(audit.status)) {
    return false;
  }

  // Check if all todos are approved or rejected
  return todos.every(
    (todo) =>
      todo.status === TODO_STATUS.APPROVED ||
      todo.status === TODO_STATUS.REJECTED
  );
}

// ============================================
// STATE TRANSITIONS
// ============================================

/**
 * Get new todo status when updating
 */
export function getNewTodoStatus(
  currentStatus: string,
  auditted: string,
  userRole: string
): string {
  // For VendorUser and VendorAdmin
  if (
    userRole === USER_ROLE.VENDOR_USER ||
    userRole === USER_ROLE.VENDOR_ADMIN
  ) {
    // Pending with no audit/physical audit => Approved
    if (
      currentStatus === TODO_STATUS.PENDING &&
      (auditted === AUDITTED_TYPE.NO_AUDIT ||
        auditted === AUDITTED_TYPE.PHYSICAL_AUDIT)
    ) {
      return TODO_STATUS.APPROVED;
    }

    // Any other transition => ActivitySaved
    return TODO_STATUS.ACTIVITY_SAVED;
  }

  // For AuditorUser and AuditorAdmin - status is set explicitly in UI
  return currentStatus;
}

/**
 * Get new audit status after todo update
 */
export function getNewAuditStatusAfterTodoUpdate(
  currentAuditStatus: string,
  userRole: string
): string | null {
  if (
    userRole !== USER_ROLE.VENDOR_USER &&
    userRole !== USER_ROLE.VENDOR_ADMIN
  ) {
    return null; // Auditors don't trigger audit status changes on todo update
  }

  // From preliminary_audit_finalized group => preliminary_audit_pending
  const preliminaryFinalizedGroup = [
    AUDIT_STATUS.PRELIMINARY_AUDIT_FINALIZED,
    AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_UPLOADING,
    AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_FAILED
  ];

  // commented to remove error
  // if (preliminaryFinalizedGroup.includes(currentAuditStatus)) {
  //   return AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING;
  // }

  // From final_audit_editing group => final_audit_pending
  const finalEditingGroup = [
    AUDIT_STATUS.FINAL_AUDIT_EDITING,
    AUDIT_STATUS.FINAL_AUDIT_PUBLISHED,
    AUDIT_STATUS.FINAL_AUDIT_REPORT_FAILED
  ];

  // commented to remove error
  // if (finalEditingGroup.includes(currentAuditStatus)) {
  //   return AUDIT_STATUS.FINAL_AUDIT_PENDING;
  // }

  return null; // No change needed
}

/**
 * Get new audit status after submitting to auditor
 */
export function getNewAuditStatusAfterSubmit(
  currentAuditStatus: string
): string {
  switch (currentAuditStatus) {
    case AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING:
      return AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED;
    case AUDIT_STATUS.FINAL_AUDIT_EDITING:
    case AUDIT_STATUS.FINAL_AUDIT_PENDING:
      return AUDIT_STATUS.FINAL_AUDIT_SUBMITTED;
    default:
      return currentAuditStatus;
  }
}

/**
 * Determine if this is first time publish (preliminary) or republish (final)
 */
export function isFirstTimePublishCheck(auditStatus: any): boolean {
  const preliminaryStatuses = [
    AUDIT_STATUS.FINAL_AUDIT_EDITING,
    AUDIT_STATUS.FINAL_AUDIT_PUBLISHED,
    AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED
  ];
  return !preliminaryStatuses.includes(auditStatus);
}

/**
 * Check if audit is in preliminary phase
 */
export function isInPreliminaryPhase(auditStatus: any): boolean {
  return [
    AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING,
    AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED,
    AUDIT_STATUS.PRELIMINARY_AUDIT_FINALIZED,
    AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_UPLOADING,
    AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_FAILED
  ].includes(auditStatus);
}

/**
 * Check if audit is in final phase
 */
export function isInFinalPhase(auditStatus: any): boolean {
  return [
    AUDIT_STATUS.FINAL_AUDIT_PENDING,
    AUDIT_STATUS.FINAL_AUDIT_EDITING,
    AUDIT_STATUS.FINAL_AUDIT_SUBMITTED,
    AUDIT_STATUS.FINAL_AUDIT_PUBLISHED,
    AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADING,
    AUDIT_STATUS.FINAL_AUDIT_REPORT_FAILED,
    AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED
  ].includes(auditStatus);
}
